// Tail rest-curve generator. Produces world-space target positions for
// each tail node from a per-bone *delta-pitch* array — one bend (rad)
// per joint, applied cumulatively as the chain walks.
//
// Each `pitches[i]` is the additional bend at the i-th joint, relative
// to the previous bone's direction (or to the body's −Z back-axis for
// i=0). The cumulative angle at bone i is `sum(pitches[0..i])`. This
// matches the parent-child bone hierarchy (rotating a base bone
// rotates everything below it with it) and is easier to reason about
// per-slider — large local bends are large local numbers.
//
// Per-bone control lets the tail express both the squirrel "major
// curl" (the sharp lift through the lower-middle of the tail) and the
// "minor curl" (the tip hooking forward over the back) at independent
// positions along the chain — shapes a smooth Bézier or a piecewise-
// linear-with-N-control-points couldn't dial in cleanly.
//
// Layered on top: a low-amplitude travelling wave that grows toward the
// tip, so the tail isn't perfectly still even when the body is — the
// signature "alive but settled" idle. Pure functions; all the random/
// time inputs are explicit. Mutates an output buffer in place to avoid
// per-frame allocations.
//
// Output is consumed by spring.ts as `restTargets`. The spring chain
// pulls toward these positions while still letting body motion perturb
// the tail dynamically — controlled when settled, whippy when moving.

import type { Vec3 } from './ik';

export interface TailRestCurveParams {
  /**
   * Per-joint bend (rad), applied cumulatively. `pitches[i]` is the
   * additional bend at joint i, relative to the previous bone's
   * direction (or horizontal-back for i=0). The bone's absolute pitch
   * in body frame is `sum(pitches[0..i])`. If the array is shorter
   * than `segments`, trailing joints take 0 (no further bend) so the
   * tail extends straight beyond the last specified joint rather than
   * spiralling.
   */
  pitches: readonly number[];
  /** Length of each tail segment, in world units. */
  segmentLength: number;
  /** Number of tail segments (= curve points after the root anchor). */
  segments: number;
  /** Idle sway amplitude (m), peak displacement at the tip. */
  idleAmp: number;
  /** Idle sway temporal frequency (Hz). */
  idleFreq: number;
  /**
   * Spatial cycles across the tail length. < 1 = single bow travelling
   * down the tail; ~1.5 = visible S-wave. Squirrel idle reads best at
   * <1 cycle (one slow undulation).
   */
  idleWaveCycles: number;
  /**
   * Body-frame X offset applied to the rest target, scaled linearly
   * from 0 at the root to `lateralBias` at the tip. Used by the
   * designer to drive turn-induced tail swing: the body's smoothed
   * heading rate times a tunable response coefficient. When the body
   * stops turning the bias decays to 0 and the tail settles back to
   * its centred carry shape.
   */
  lateralBias: number;
}

function bendForJoint(i: number, pitches: readonly number[]): number {
  return i < pitches.length ? pitches[i] : 0;
}

/**
 * Compute tail rest-curve world positions.
 *
 * `out` is an array of length `segments + 1`. `out[0]` is the anchor
 * (set to `hipWorld`); subsequent indices are each tail node's target
 * position in world space, with idle sway applied.
 *
 * The body-frame curve is built by stepping a unit vector pitched up
 * from the body's −Z (back) axis by the corresponding bone's pitch,
 * accumulating segmentLength per step. A heading rotation around Y
 * transforms body-frame to world.
 */
export function computeTailRestCurve(
  hipWorld: Vec3,
  heading: number,
  time: number,
  params: TailRestCurveParams,
  out: Array<[number, number, number]>,
): void {
  const N = params.segments;
  const cosH = Math.cos(heading);
  const sinH = Math.sin(heading);

  out[0] = [hipWorld[0], hipWorld[1], hipWorld[2]];

  // Body-frame accumulated position. The chain's joint pitches only
  // produce Y/Z motion; lateral X is folded in per-bone below.
  let by = 0;
  let bz = 0;

  let pitch = 0;

  for (let i = 0; i < N; i++) {
    // Accumulate the joint bend onto the running absolute pitch. Each
    // bone inherits its parent's direction; only the delta is in the
    // pitches array.
    pitch += bendForJoint(i, params.pitches);

    // Body-frame direction: from −Z (straight back), pitched up by
    // `pitch` around X. pitch = 0 → (0, 0, −1); pitch = π/2 → (0, 1, 0);
    // pitch > π/2 curls forward over the back.
    const dirY = Math.sin(pitch);
    const dirZ = -Math.cos(pitch);

    by += dirY * params.segmentLength;
    bz += dirZ * params.segmentLength;

    // Lateral bias: body-frame X offset linearly weighted by distance
    // along the tail (root unaffected, tip gets the full bias). The
    // designer drives this from heading rate, so the rest target
    // shifts laterally during turns and returns to centred at rest.
    const t = N === 1 ? 1 : (i + 1) / N;
    const lateral = params.lateralBias * t;

    // Idle sway: vertical bob whose amplitude grows linearly with the
    // bone's distance along the tail, travelling at the configured
    // spatial frequency.
    const sway =
      params.idleAmp *
      t *
      Math.sin(2 * Math.PI * (params.idleFreq * time - t * params.idleWaveCycles));

    // Body → world via heading (yaw around +Y). bx is unused (always 0
    // in our chain); lateral plays the body-frame X role.
    const wx = hipWorld[0] + lateral * cosH + bz * sinH;
    const wy = hipWorld[1] + by + sway;
    const wz = hipWorld[2] - lateral * sinH + bz * cosH;

    out[i + 1] = [wx, wy, wz];
  }
}
